import { durationForTicks, ticks, type Duration } from '@simplenotation/theory';
import type { BeatRef, Score } from '../model/types';
import { createRest, emptyTrackBar, barRests } from '../model/factories';
import { voiceOf, filledTicks, barCapacityAt, timeSigAt } from '../model/query';
import { type EditorState, type Lane, mutate, focusBeat, draftVoice, selectionRange } from './state';

/** Rest duration for a new beat: the input duration, clamped to the space left in the bar when possible. */
function newBeatDuration(st: EditorState, bar: number, filled: number): Duration {
  const cap = barCapacityAt(st.score, bar);
  const want = st.duration;
  const left = cap - filled;
  if (ticks(want) > left && left > 0) {
    const d = durationForTicks(left);
    if (d) return d;
  }
  return { ...want };
}

/** Append a bar at the end of the score (all tracks get bar rests). */
export function appendBarDraft(d: Score): void {
  const last = d.bars.length - 1;
  const ts = timeSigAt(d, last);
  const wasFinal = d.bars[last].barline === 'final';
  if (wasFinal) delete d.bars[last].barline;
  d.bars.push(wasFinal ? { barline: 'final' } : {});
  for (const t of d.tracks) t.bars.push(emptyTrackBar(ts));
}

/**
 * Move right (§4.6): next beat; past the end of an incomplete bar append a rest
 * of the input duration; past the end of the last full bar create a bar.
 */
export function moveRight(st: EditorState): EditorState {
  const c = st.cursor;
  const beats = voiceOf(st.score, c);
  if (c.beat < beats.length - 1) return focusBeat(st, { ...c, beat: c.beat + 1 });
  const filled = filledTicks(beats);
  const cap = barCapacityAt(st.score, c.bar);
  if (filled < cap) {
    const dur = newBeatDuration(st, c.bar, filled);
    const next = mutate(st, (d) => {
      draftVoice(d, c).push(createRest(dur));
    });
    return focusBeat(next, { ...c, beat: beats.length });
  }
  if (c.bar === st.score.bars.length - 1) {
    const next = mutate(st, (d) => {
      appendBarDraft(d as Score);
      // The cursor track's new bar holds just a rest of the input duration.
      const tb = d.tracks[c.track].bars[c.bar + 1];
      tb.voices = [];
      const v = draftVoice(d, { track: c.track, bar: c.bar + 1, voice: c.voice });
      v.push(createRest({ ...st.duration }));
      if (c.voice > 0) {
        // Voice 0 keeps a bar rest so the bar is never empty.
        tb.voices[0] = barRests(timeSigAt(d as Score, c.bar + 1));
      }
    });
    return focusBeat(next, { ...c, bar: c.bar + 1, beat: 0 });
  }
  return focusBeat(st, { ...c, bar: c.bar + 1, beat: 0 });
}

export function moveLeft(st: EditorState): EditorState {
  const c = st.cursor;
  if (c.beat > 0) return focusBeat(st, { ...c, beat: Math.min(c.beat - 1, voiceOf(st.score, c).length) });
  if (c.bar === 0) return focusBeat(st, c);
  const prev = voiceOf(st.score, { ...c, bar: c.bar - 1 });
  return focusBeat(st, { ...c, bar: c.bar - 1, beat: Math.max(0, prev.length - 1) });
}

/** Mod+←/→: first beat of the previous/next bar. */
export function moveBar(st: EditorState, dir: 1 | -1): EditorState {
  const c = st.cursor;
  if (dir < 0 && c.beat > 0) return focusBeat(st, { ...c, beat: 0 });
  const bar = Math.max(0, Math.min(st.score.bars.length - 1, c.bar + dir));
  return focusBeat(st, { ...c, bar, beat: 0 });
}

export function moveToStart(st: EditorState): EditorState {
  return focusBeat(st, { ...st.cursor, bar: 0, beat: 0 });
}

export function moveToEnd(st: EditorState): EditorState {
  const bar = st.score.bars.length - 1;
  const beats = voiceOf(st.score, { ...st.cursor, bar });
  return focusBeat(st, { ...st.cursor, bar, beat: Math.max(0, beats.length - 1) });
}

/** Shift+←/→: extend the range selection by one beat. */
export function extendSelection(st: EditorState, dir: 1 | -1): EditorState {
  const c = st.cursor;
  const anchor = st.selection?.anchor ?? { bar: c.bar, beat: c.beat };
  let { bar, beat } = c;
  const beats = voiceOf(st.score, c);
  if (dir > 0) {
    if (beat < beats.length - 1) beat++;
    else if (bar < st.score.bars.length - 1) {
      bar++;
      beat = 0;
    }
  } else if (beat > 0) beat--;
  else if (bar > 0) {
    bar--;
    beat = Math.max(0, voiceOf(st.score, { ...c, bar }).length - 1);
  }
  const next = focusBeat(st, { ...c, bar, beat }, true);
  return { ...next, selection: { ...st.selection, anchor, focus: { bar, beat } } };
}

/** Shift+↑/↓: extend the selection to neighbouring tracks. */
export function extendSelectionVertical(st: EditorState, dir: 1 | -1): EditorState {
  const r = selectionRange(st);
  const sel = st.selection ?? { anchor: r.start, focus: r.end };
  const tf = sel.trackFrom ?? st.cursor.track;
  let tt = sel.trackTo ?? st.cursor.track;
  tt = Math.max(0, Math.min(st.score.tracks.length - 1, tt + dir));
  return { ...st, selection: { ...sel, trackFrom: tf, trackTo: tt } };
}

export function selectAll(st: EditorState): EditorState {
  const lastBar = st.score.bars.length - 1;
  const beats = voiceOf(st.score, { ...st.cursor, bar: lastBar });
  return {
    ...st,
    selection: { anchor: { bar: 0, beat: 0 }, focus: { bar: lastBar, beat: Math.max(0, beats.length - 1) } },
  };
}

export function selectBars(st: EditorState, from: number, to: number): EditorState {
  const beats = voiceOf(st.score, { ...st.cursor, bar: to });
  const next = focusBeat(st, { ...st.cursor, bar: from, beat: 0 });
  return { ...next, selection: { anchor: { bar: from, beat: 0 }, focus: { bar: to, beat: Math.max(0, beats.length - 1) } } };
}

export function clearSelection(st: EditorState): EditorState {
  return st.selection ? { ...st, selection: null } : st;
}

/** ↑/↓: staff position (staff lane) or string (tab lane). `dir` = +1 for up. */
export function moveVertical(st: EditorState, dir: 1 | -1): EditorState {
  const c = st.cursor;
  const track = st.score.tracks[c.track];
  if (c.lane === 'tab') {
    const strings = track.tuning?.length ?? 6;
    const string = c.string - dir;
    if (string < 1) {
      // Above string 1: move to the staff if shown.
      if (track.showStaff) return { ...st, cursor: { ...c, lane: 'staff' } };
      return st;
    }
    if (string > strings) return st;
    return { ...st, cursor: { ...c, string } };
  }
  const staffPos = c.staffPos + dir;
  if (staffPos < -14 && track.showTab) return { ...st, cursor: { ...c, lane: 'tab', string: 1 } };
  return { ...st, cursor: { ...c, staffPos: Math.max(-20, Math.min(20, staffPos)) } };
}

/** Place the cursor (from a click / hit test). */
export function setCursor(
  st: EditorState,
  ref: BeatRef,
  opts: { lane?: Lane; string?: number; staffPos?: number; extend?: boolean } = {},
): EditorState {
  const c = st.cursor;
  const lane = opts.lane ?? c.lane;
  if (opts.extend) {
    const anchor = st.selection?.anchor ?? { bar: c.bar, beat: c.beat };
    const next = focusBeat({ ...st, cursor: { ...c, lane } }, ref, true);
    return { ...next, selection: { anchor, focus: { bar: ref.bar, beat: ref.beat } } };
  }
  let next = focusBeat({ ...st, cursor: { ...c, lane } }, ref);
  next = {
    ...next,
    cursor: {
      ...next.cursor,
      string: opts.string ?? next.cursor.string,
      staffPos: opts.staffPos ?? next.cursor.staffPos,
    },
  };
  return next;
}

/** Mod+1…4: switch the input voice; the cursor goes to the beat at the same onset or the voice's end. */
export function setVoice(st: EditorState, voice: number): EditorState {
  const c = st.cursor;
  if (voice === c.voice) return st;
  const beats = voiceOf(st.score, { ...c, voice });
  const onset = (() => {
    let t = 0;
    const cur = voiceOf(st.score, c);
    for (let i = 0; i < c.beat && i < cur.length; i++) t += cur[i].grace ? 0 : ticks(cur[i].duration);
    return t;
  })();
  let beat = beats.length; // insertion point
  let t = 0;
  for (let i = 0; i < beats.length; i++) {
    if (t >= onset) {
      beat = i;
      break;
    }
    t += beats[i].grace ? 0 : ticks(beats[i].duration);
  }
  if (beats.length === 0) beat = 0;
  return focusBeat({ ...st, selection: null }, { ...c, voice, beat });
}

export function setLane(st: EditorState, lane: Lane): EditorState {
  return { ...st, cursor: { ...st.cursor, lane } };
}
